import { AUTH_TYPE } from "@/constants";
import { useNotificationError } from "@/context/error-provider";
import { useMHccb } from "@/hooks/use-ebix";
import useUserInfo from "@/hooks/use-user-info";
import type { ReactNode } from "react";
import { ErrorNotificationType } from "../error-notification";

type HccbFeature = {
  action?: { sso_id?: string };
  order?: number;
  icon?: string;
  name?: string;
};

type HccbMutationResponse = {
  data: {
    SAMLUrl?: string;
    SAMLResponse?: string;
    RelayState?: string;
    AuthType?: string;
  } | null;
  summary?: { message?: string };
};

type HccbWrapperProps = {
  feature: HccbFeature;
  children: (props: { onClick: () => void; isPending: boolean }) => ReactNode;
};

const HccbWrapper = ({ feature, children }: HccbWrapperProps) => {
  const userInfo = useUserInfo();
  const hccbMQuery = useMHccb(feature?.action?.sso_id || "");
  const { addError } = useNotificationError();

  const loadLinModule = (response: HccbMutationResponse) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = response.data?.SAMLUrl ?? "";
    form.target = "_blank";

    // Create a hidden input for SAMLResponse
    const samlInput = document.createElement("input");
    samlInput.type = "hidden";
    samlInput.name = "SAMLResponse";
    samlInput.value = response?.data?.SAMLResponse ?? "";
    form.appendChild(samlInput);

    // Create a hidden input for RelayState (if needed)
    const relayStateInput = document.createElement("input");
    relayStateInput.type = "hidden";
    relayStateInput.name = "RelayState";
    relayStateInput.value = response?.data?.RelayState ?? "";
    form.appendChild(relayStateInput);

    // Append the form to the body

    document.body.appendChild(form);

    // Submit the form
    form.submit();

    // Remove the form from the DOM
    document.body.removeChild(form);
  };

  const redirectToHccb = () => {
    hccbMQuery.mutate(
      { info: userInfo },
      {
        onSuccess(data) {
          if (data.data) {
            if (
              data.data.AuthType?.toUpperCase() === AUTH_TYPE.REST &&
              data.data.SAMLUrl
            ) {
              window.open(data.data.SAMLUrl, "_blank", "noopener,noreferrer");
              return;
            }

            loadLinModule(data);
          } else {
            addError({
              message: data.summary?.message ?? "Something went wrong",
              type: ErrorNotificationType.ERROR,
            });
          }
        },
        onError(error) {
          addError({
            message: error.message,
            type: ErrorNotificationType.ERROR,
          });
        },
      },
    );
  };

  return (
    <>
      {children({ onClick: redirectToHccb, isPending: hccbMQuery.isPending })}
    </>
  );
};

export default HccbWrapper;
